/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, Inject } from '@angular/core';
// import { Router } from '@angular/router';

import { UndoCache } from './undo-cache';
import { BehaviorSubject, generate, Subject } from 'rxjs';
import { filter, last, concatMap, map, switchMap, tap, pairwise, startWith } from 'rxjs/operators';
import { RouteUtils } from 'src/app/services/route-utils.service';
import { UndoItem } from './UndoItem';
import { UndoTypes } from './UndoTypes';

import { UndoOperationInterface } from './UndoOperationInterface';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../logging/LoggerServiceFactory';

// ### Player 1: Undo Service
// - responsible for marshelling the undo data, transfering data between the operation and the cache
// - provides methods to the ui for restoring an undo
// - provides data to the ui of the available operations, their names, the selected one, etc.
// - stores the invalidation cache
// - does _not_ have any understanding of the shape of an operation on the undo cache or the invalidation operation cache.
// It's just a blob of data.

//TODO - the file structure here is very temporary
// can't honestly think of any reason this _shouldn't_ just be an angular service

//DATA FLOW DOC
// general
// - operations don't have access to service, service has access to operations
// - "client" code has access to both

// creating an undo item data flow
// - UndoOperation listens to data via some method, subscribes to observables, provides methods for calling it with data, etc
// - UndoOperation takes that data and forms it into an UndoItem compatible object
// - UndoOperation calls the onPush method which is decorated onto the operation by the service
// - UndoService takes the provided UndoItem and pushes it into the cache (the cache is a black box). It also resets the indexSubject$ to empty and clears out any "above" UndoItem's in the list
// - UndoCache will emit an update to it's skeletonList$ with the updated data as a result of pushing
// - UndoService subscribes to skeletonList$, on change it subscribes internally
// - UndoService calls recalcCombinedInvalidationCache to call UndoOperation.calculateCombinedInvalidation
//	 to recalculate the combinedInvalidationCache and stores it

// invalidating an undo item data flow
// - UndoOperation listens to data via some method, subscribes to observables, provides methods for calling it with data, etc
// - UndoOperation takes that data and calls onVerifyInvalidation which is decorated onto the operation by the service
// - UndoService calls checkForInvalidation which gets the type specific invalidation cache and calls the callback passed in from onVerifyInvalidation
// - UndoOperation onVerifyInvalidation callback uses the invalidation cache to check if everything is still valid. It returns all UndoItems that have been invalidated.
// - UndoService marks the returned items as invalid and makes a metadata update to the cache
// - UndoCache will emit an update to it's skeletonList$ with the updated data as a result of pushing
// - UndoService subscribes to skeletonList$, on change it subscribes internally
// - UndoService calls recalcCombinedInvalidationCache to call UndoOperation.calculateCombinedInvalidation
//	 to recalculate the combinedInvalidationCache and stores it

// applying an undo data flow
// - consumer code call UndoService.applyUndo
// - UndoService.applyUndo accepts an index, determines the sequence of UndoItems to be executed, determines if it needs to call applyUndo or applyRedo on the UndoItem is called
// - each UndoOperation is called in sequence, one executing until completion before the next begins
// - UndoOperation applyUndo/applyRedo is called, it pushes data from the UndoItem into the main data, it should return an observable that completes when applying the undo is complete (EMPTY if it completes immediately).
// - UndoService after each UndoItem is applied, the UndoService updates the indexSubject$

@Injectable()
export class UndoService {
	private cache: UndoCache = new UndoCache(['loading']);
	/** current set of active operations. Stored in object to enforce one per type
	 * TODO - create enum for index
	 */
	private operations: { [key: string]: UndoOperationInterface } = {};
	private logger: ILoggerService;

	private listSubject$ = new BehaviorSubject<UndoItem[]>([]);
	public list$ = this.listSubject$.asObservable();
	private indexSubject$ = new BehaviorSubject(-1);
	public index$ = this.indexSubject$.asObservable();
	public get syncIndex() {
		return this.indexSubject$.getValue();
	}

	private applyInProgressSubject$ = new BehaviorSubject(false);
	/** can be used for "plan blocking measures". Emits true when an undo is in progress.
	 * False when it isn't.
	 */
	public applyInProgres$ = this.applyInProgressSubject$.asObservable();
	public get syncApplyInProgress() {
		return this.applyInProgressSubject$.getValue();
	}

	//exact shape tbd
	// - this has to live in memory for fast access, would have to get really creative to do otherwise
	// - it's likely that this will achieve sufficient complexity to be broken out into a class, start with an object though
	// - top level is an object-like where each key is an UndoType and the value is the next level
	// - that level contains an object with the combined invalidation data and the list of per-item invalidation data
	// ROUND 2 thoughts
	// - nix the nested list, that list can just be list$, since that's just going to be part of the skeleton list, which is always in memory
	private invalidationCache: { [key: string]: any } = {};

	//TODO - routeUtils needs to be extracted out into a common module
	constructor(private routeUtils: RouteUtils, private featureFlagManager: FeatureFlagManager) {
		if (!featureFlagManager.getFlag(DynamicFlagTypes.UNDO)) {
			return;
		}
		this.logger = LoggerServiceFactory.create('UndoService');
		this.monitorRoute();
	}

	private monitorRoute() {
		//this emits the previous cache [0] and the current cache [1]
		//the subscriber is expected to apply that to the state
		const cache$ = this.routeUtils.snapshot.pipe(
			startWith(null as any), //start with ensure it emits on the first route
			map((snapshot) => (snapshot ? new UndoCache(snapshot.url.map((a) => a.path)) : null)),
			pairwise()
		);
		cache$.subscribe((caches) => {
			if (!caches[1]) {
				return;
			} //no-op, caused by behavior subject defaults
			if (caches[0]) {
				caches[0].destroy();
			}
			this.cache = caches[1];

			//clean up all existing operations on route change
			//TODO - debug execution order...
			// this.removeAllOperations();

			//special syntax here for forwarding "just" the next emission and the error emission
			//this allows for completion of each routes cache, while the undo service one does not complete
			//materialize and dematerialize will not work in this cases because it won't clean up properly
			//The subscribe within the subscribe is very delibrate here, it makes more sense to run all the state operations
			// from a single point then doing some within the pipe and some in the subscribe.
			// Avoids the anti-pattern by ensuring the prev completes
			this.cache.skeletonList$.subscribe(
				(list) => {
					this.listSubject$.next(list);
				},
				(error) => this.listSubject$.error(error)
			);
		});

		//subscription for the invalidation cache

		//TODO - this is too slow, need to make more efficient
		// NOTES
		// - undoItem added, update the combinedCache in operation code using the new item, the combinedCache and potentially the rest of the items
		// - (might not be possible) size of the skeleton list decreases (items removed), recalc the combinedCache for that type using the combinedCache and the item list
		// - (might not be possible) size of the skeleton list increases, recalc the whole thing, or apply undoItem once per item
		// - size of the skeleton list increases and decreases at the same time, recalc the whole thing or do some sort diff between the old and new versions

		// - skeleton list "changes", recalc the whole thing, pass the current combinedCache, the list, and possibly a "previous list" if such a thing proves more efficient (duplicating minimum the array container)
		// - still need to work through type differentiation, would rather not iterate the entire list when only one type was changed
		this.listSubject$
			.pipe(
				// filter(list => list.length > 2),
				//groups the stuff (this is where most of the optimization needs to go)
				map((list) => {
					const group: { [key: string]: UndoItem[] } = {};
					list.forEach((item) => {
						if (item.isInvalid) {
							return;
						}
						if (!group[item.undoType]) {
							group[item.undoType] = [];
						}
						group[item.undoType].push(item);
					});
					return group;
				}),
				//converts groups into combined cache data
				map((group) => {
					const newCaches: { [key: string]: any } = {};
					Object.keys(group).forEach((type) => {
						newCaches[type] = this.recalcCombinedInvalidationCache(
							type as UndoTypes,
							group[type],
							this.invalidationCache[type]
						);
					});
					return newCaches;
				})
			)
			.subscribe((group) => {
				//currently undecided if I want this to live sync or as an observable... start sync?
				this.invalidationCache = group;
			});
	}

	//returns null if the operation hasn't been loaded
	private recalcCombinedInvalidationCache(undoType: UndoTypes, list: UndoItem[], existingCache: any) {
		if (!this.operations[undoType]) {
			return null;
		}
		return this.operations[undoType].calculateCombinedInvalidation(list, existingCache);
	}

	private removeAllOperations() {
		if (!this.operations) {
			return;
		}

		for (const key in this.operations) {
			if (this.operations.hasOwnProperty(key)) {
				const element = this.operations[key];
				this.unRegisterOperation(element);
			}
		}
	}

	public registerOperation(operation: UndoOperationInterface) {
		//decorate with interface lifecycle methods. tbd
		operation.registerOnPush((data) => {
			this.pushToCache(data);
		});
		operation.registerVerifyInvalidation((cb) => {
			this.checkForInvalidation(operation.undoType, cb);
		});
		operation.registerLock((lock) => {
			this.lock(lock);
		});

		//add to operations list
		this.operations[operation.undoType] = operation; //placeholder
	}

	public unRegisterOperation(operation: UndoOperationInterface) {
		operation.destroy();
		delete this.operations[operation.undoType];
	}

	public applyUndo(idx: number): Promise<void> {
		//sanity checks
		if (idx <= -1 || idx >= this.listSubject$.getValue().length) {
			return Promise.reject(new Error('index is invalid'));
		}
		const outputSubject = new Subject<Error>();

		// debugger;
		const currentIdx = this.indexSubject$.getValue();
		// this.logger.log('undo values test', currentIdx + 1 * Math.sign(idx - currentIdx ), ' - ', idx);

		const goingDown = idx > currentIdx;
		//selected index is created for the undo operation iteration, where -1 is invalid
		const selectedIndex = idx;

		//it feels like it's sane to bump the index to the item before the one you click in all cases when moving up
		//	same item works, first item works (-1). Apply the index to the one clicked when going down
		//- idx > currentIdx, going down still
		//- going down counts from currentIdx + 1 to idx
		//- going up counts from currentIdx to idx (then sets the current index to idx - 1)

		const undoPromise = new Promise<void>((resolve, reject) => {
			this.applyInProgressSubject$.next(true);
			generate(
				goingDown ? currentIdx + 1 : currentIdx,
				(x) => (goingDown ? x <= selectedIndex : x >= selectedIndex),
				(x) => (goingDown ? x + 1 : x - 1)
			)
				.pipe(
					// tap(x => this.logger.log('generate output', x)),
					//skip invalidated items
					filter((index) => this.listSubject$.getValue() && !this.listSubject$.getValue()[index].isInvalid),
					concatMap((index) => {
						return this.cache.baseRead(index).pipe(
							last(),
							// tap(x => this.logger.log('item read from cache', x)),
							tap(() => this.lock(true)),
							switchMap((cacheContents: UndoItem) => {
								if (!this.operations[cacheContents.undoType]) {
									throw new Error('undo type ' + cacheContents.undoType + ' has not be registered');
								}
								if (!goingDown) {
									return this.operations[cacheContents.undoType].applyRedo(cacheContents);
								}
								return this.operations[cacheContents.undoType].applyUndo(cacheContents);
							})
							// tap(x => this.logger.log('operation finished applying', x)),
						);
					})
				)
				.subscribe({
					error: (e) => {
						this.applyInProgressSubject$.next(false);
						reject(e);
					},
					complete: () => {
						this.applyInProgressSubject$.next(false);
						resolve();
					},
				});

			//this may want to be set only on _completion_
			this.indexSubject$.next(goingDown ? idx : idx - 1);
		});

		return undoPromise.then(() => this.lock(false));
	}

	//todo - rename
	private pushToCache(data: UndoItem) {
		// this.logger.log('push to cache', data);
		if (this.indexSubject$.getValue() !== -1) {
			this.cache.removePrevious(this.indexSubject$.getValue());
			this.indexSubject$.next(-1);
		}
		this.cache.push(data);
		// this.pushToInvalidationCache(data);
	}

	private checkForInvalidation(undoType: UndoTypes, cb) {
		const syncList = this.listSubject$.getValue();
		//no point to check the operation if there's no list or cache
		if (!this.invalidationCache || !this.invalidationCache[undoType] || !syncList || !syncList.length) {
			return;
		}

		const filteredList = this.listSubject$
			.getValue()
			.filter((value) => value.undoType === undoType && !value.isInvalid);
		const result = cb(this.invalidationCache[undoType], filteredList);
		if (!result) {
			return;
		}
		// debugger;
		result.forEach((item, resultIdx, arr) => {
			const idx = syncList.indexOf(item);
			if (idx === -1) {
				throw new Error('Undo operation verify invalidation returned invalid value');
			}
			item.isInvalid = true;
			this.cache.metaUpdate(idx, item, resultIdx < arr.length - 1);
		});

		// const indexes = result.map(item => syncList.indexOf(item));
		// Logging.warning('undo operation invalidated');
		// this.cache.remove(indexes);
	}

	private lock(lock: boolean) {
		this.applyInProgressSubject$.next(lock);
	}
}
